/**
 * Les deux modules de SwissTech, Bâtiment et Magasin, et ce que chacun ouvre.
 *
 * <p>Une société abonnée au Bâtiment ne voit et n'utilise que le Bâtiment ;
 * une société Magasin, que le Magasin ; une société Mix, les deux, avec la
 * bascule de l'en-tête. Le serveur applique la même règle (`ModuleFilter`) :
 * une route de l'autre module répond 403 `module-non-souscrit`.
 *
 * <p>Ces listes sont la seule source : le menu Plus, la garde des routes de
 * `App` et le chargement des données s'y réfèrent.
 */
import { roleCan } from './access';

/**
 * Écrans propres au Bâtiment : absents du Magasin. Le bon de commande est
 * celui d'un chantier ; le magasin a ses commandes clients.
 */
export const ROUTES_BATIMENT = ['chantiers', 'planning', 'modeles', 'soumissions', 'bonc'];
// Les bons d'achat (pour le stock, sans chantier), la décharge A4 et le GPS
// de l'équipe (les livreurs) servent aussi au magasin : ils sont des deux.

/**
 * Écrans propres au Magasin : absents du Bâtiment. Le Bâtiment garde son
 * « Stock / Dépôt » (`stock`), sans la caisse, les remises ni les commandes
 * de ses clients.
 */
export const ROUTES_MAGASIN = ['ventes', 'remises', 'commandes', 'livraisons', 'ouverture'];

/**
 * Le secteur d'une société désigne-t-il un module ? « chantier » est un ancien
 * mot du bâtiment, que des fiches portent encore.
 *
 * @param {string|null|undefined} secteur secteur enregistré sur la société
 * @returns {{batiment: boolean, magasin: boolean}} les modules souscrits
 */
export function modulesSouscrits(secteur) {
  const s = String(secteur || '').toLowerCase();
  return {
    batiment: s === 'batiment' || s === 'chantier' || s === 'mixte',
    magasin: s === 'magasin' || s === 'mixte',
  };
}

/**
 * L'univers affiché : le module de la société, ou en Mix celui de la bascule.
 *
 * @param {object} options
 * @param {object|null} options.soc société (le secteur réécrit par `App` en Mix)
 * @param {boolean} [options.estMixte] vrai pour une société Mix
 * @param {string} [options.univers] `chantier` ou `magasin`, en Mix
 * @returns {boolean} vrai dans l'univers Magasin
 */
export function universMagasin({ soc, estMixte, univers }) {
  return estMixte ? univers === 'magasin' : soc?.secteur === 'magasin';
}

/**
 * Un écran est-il ouvert dans l'univers affiché ?
 *
 * @param {string} route écran demandé
 * @param {boolean} mag vrai dans l'univers Magasin
 * @returns {boolean} faux pour un écran de l'autre module
 */
export function routeDuModule(route, mag) {
  return mag ? !ROUTES_BATIMENT.includes(route) : !ROUTES_MAGASIN.includes(route);
}

/**
 * La route à afficher : celle demandée si le rôle y a droit et qu'elle
 * appartient à l'univers affiché, sinon la route de repli. C'est la garde des
 * routes d'`App`.
 *
 * @param {string} route route demandée
 * @param {boolean} mag vrai dans l'univers Magasin (en Mix, celui de la bascule)
 * @param {string} repli route par défaut du rôle
 * @returns {string} la route retenue
 */
export function routeAffichee(route, mag, repli) {
  return roleCan(route) && routeDuModule(route, mag) ? route : repli;
}
